import DOMPurify from 'dompurify'
import { TypedSalary } from '../../../../schema/job'
import { RemoteWork } from '../../../../service/util/master-data/masterdata'
import type { MinMax } from '../../../../stories/atoms/MinMaxInput/MinMaxInput'
import { OptionOrString } from '../../../../stories/atoms/SelectOrFreeTextMenu/SelectOrFreeTextMenu'
import { ApplyViaType } from '../../../../stories/organisms/CompanyForm/CompanyContact/CompanyContact'
import { Validator } from '../../../../util/useForm/useForm'
import { noValidation, validateRequiredOption, Validity } from '../../../../util/validate/validate'
import { MAX_SALARY, MIN_SALARY } from '../../[id]/utils'

const purify = DOMPurify()
const sanitize = purify.sanitize

/**
 * Generic validation functions
 */

const isNumberWhole = (value: number) => !Boolean(value % 1)

export const validateMinMax =
  ({
    min,
    max,
    message = '',
    allowDecimal = false,
  }: {
    min: number
    max: number
    message?: string
    allowDecimal?: boolean
  }) =>
  ({ min: newMin, max: newMax }: MinMax): Validity => {
    const maxValue = Number(newMax)
    const minValue = Number(newMin)

    if ((maxValue === 0 && newMax.match(/^0+$/)) || (minValue === 0 && newMin.match(/^0+$/))) {
      return [false, 'De waarde moet groter zijn dan 0']
    }

    if (maxValue > max) {
      return [false, message || `Het getal moet kleiner zijn dan ${max}`]
    }

    if (maxValue < (minValue || min)) {
      return [false, message || 'Het maximumbedrag moet hoger zijn dan het minimumbedrag']
    }

    if (minValue < min) {
      return [false, message || `Het getal moet groter zijn dan ${min}`]
    }

    if (!allowDecimal) {
      const decimalsValidation = isNumberWhole(minValue) && isNumberWhole(maxValue)

      if (!decimalsValidation) {
        return [false, 'Alleen hele getallen zijn toegestaan']
      }
    }

    return [true, '']
  }

export const validateRequiredMinMax =
  ({ min, max, message = '' }: { min: number; max: number; message?: string }) =>
  ({ min: newMin, max: newMax }: MinMax): Validity => {
    if (newMin === '' || newMax === '') {
      return [
        false,
        message || 'Vul beide velden in. Het maximale bedrag moet altijd hoger liggen dan het minimale bedrag.',
      ]
    }
    return validateMinMax({ min, max, message })({ min: newMin, max: newMax })
  }

const validateUrl = (value: string): Validity => {
  if (value.length === 0) return [false, 'Vul een URL in.']
  try {
    /* eslint-disable no-new */
    new URL(value)
  } catch (e) {
    return [false, 'Zorg voor een geldige URL startend met https://.']
  }
  return [true, '']
}

/**
 * Specific validation functions
 */

export function validateTitle(value: string): Validity {
  if (value.length === 0) {
    return [false, 'Vul de vacaturetitel in.']
  }
  if (value.length > 200) {
    return [
      false,
      'Om het meeste uit ons platform te halen is het nodig om jouw vacature titel korter dan 200 karakters te houden.',
    ]
  }
  return [true, '']
}

export const validateFunctionGroupByOptions = (input: OptionOrString): Validity => {
  const { selectedOption } = input
  if (selectedOption) {
    if (selectedOption.value) {
      return [true, '']
    } else {
      return [
        false,
        'Om een prijs voor deze vacature vast te kunnen stellen hebben wij de meest passende functienaam nodig.',
      ]
    }
  } else {
    return [
      false,
      'Om een prijs voor deze vacature vast te kunnen stellen hebben wij de meest passende functienaam nodig.',
    ]
  }
}

export const validateFunctionGroup = (input: OptionOrString): Validity => {
  const { userDefinedOption, selectedOption } = input
  if (userDefinedOption) {
    // istanbul ignore else
    if (userDefinedOption?.length > 100) {
      return [
        false,
        'Om het meeste uit ons platform te halen is het nodig om jouw functienaam korter dan 100 karakters te houden.',
      ]
    }
    return [true, '']
  } else if (selectedOption?.value) {
    return [true, '']
  } else {
    return [false, 'Kies of voer de functienaam van de vacature in.']
  }
}

export const validatePlace = ({ value }): Validity => {
  if (!value) {
    return [false, 'Vul de postcode in.']
  }

  return [true, '']
}

export const validateJobCategories =
  (options: Array<string>) =>
  (value: Array<string>): Validity => {
    if (!value.every(v => options.includes(v))) {
      return [false, 'Kies een functiegroep.']
    }

    if (value.length === 0) {
      return [false, 'Vul minimaal één functiegroep in.']
    }

    if (value.length > 2) {
      return [false, 'Vul maximaal twee functiegroepen in.']
    }

    return [true, '']
  }

export const validateBranches =
  (options: Array<string>) =>
  (value: Array<string>): Validity => {
    if (!value.every(v => options.includes(v))) {
      return [false, 'Kies een branchenaam.']
    }

    if (value.length === 0) {
      return [false, 'Vul minimaal één branche in.']
    }

    if (value.length > 2) {
      return [false, 'Vul maximaal twee branches in.']
    }

    return [true, '']
  }

export const validateSalary = ({ min, max }: MinMax): Validity => {
  // Salary is required if one of the fields is filled in
  if ((min === '' && max !== '') || (min !== '' && max === '')) {
    return [false, 'Vul het minimum- en maximum salaris in']
  }
  return validateMinMax({ min: MIN_SALARY, max: MAX_SALARY, allowDecimal: true })({ min, max })
}

export const validateTypedSalary = (salary?: TypedSalary): Validity => {
  if (typeof salary === 'undefined') {
    return [true, '']
  }

  const { min, max } = salary

  if (min === undefined && max === undefined) {
    return [true, '']
  }

  if (typeof min !== 'number' || typeof max !== 'number') {
    return [false, 'Vul alleen getallen in de velden in']
  }

  if (min < 0 || max < 0) {
    return [false, 'Een salaris kan geen negatieve waarde hebben']
  }

  if (max < min) {
    return [false, 'Het maximum salaris moet altijd hoger zijn dan het minimum salaris']
  }

  const decimalsValidation = isNumberWhole(min) && isNumberWhole(max)

  if (!decimalsValidation) {
    return [false, 'Alleen hele getallen zijn toegestaan.']
  }

  return [true, '']
}

export const validateHours = validateRequiredMinMax({
  min: 1,
  max: 40,
  message: 'Vul beide velden in. Het maximale aantal uren moet altijd hoger liggen dan het minimale aantal uren.',
})

export const validateContractTypes =
  (options: Array<{ value: string }>) =>
  (value: Array<string>): Validity => {
    if (!value.every(v => options.find(o => o.value === v))) {
      return [false, 'Kies een dienstverband.']
    }

    if (value.length === 0) {
      return [false, 'Selecteer minimaal één optie.']
    }

    if (value.length > 5) {
      return [false, 'Selecteer maximaal vijf opties.']
    }

    return [true, '']
  }

export const validateEducationLevels =
  (options: Array<{ value: string }>) =>
  (value: Array<string>): Validity => {
    if (!value.every(v => options.find(o => o.value === v))) {
      return [false, 'Kies een opleidingsniveau.']
    }

    if (value.length === 0) {
      return [false, 'Selecteer minimaal één optie.']
    }

    if (value.length > 5) {
      return [false, 'Selecteer maximaal vijf opties.']
    }

    return [true, '']
  }

export const validateExperienceLevel = validateRequiredOption('Selecteer minimaal één optie.')

export function validateRemoteWork(value: RemoteWork): Validity {
  if (!Object.values(RemoteWork).includes(value)) {
    return [false, 'Vul een waarde in.']
  }

  return [true, '']
}

export function validateJobText(value: string): Validity {
  const MAX = 15000
  const message = `Schrijf je vacaturetekst, maximaal ${MAX} karakters.`

  if (value.length === 0) {
    return [false, message]
  }

  const sanitizedValue = sanitize(value, { ALLOWED_TAGS: [] })

  if (sanitizedValue.length > MAX) {
    return [false, `De vacaturetekst mag niet langer zijn dan ${MAX} karakters.`]
  }

  return [true, '']
}

export function validateVideoUrl(value: string): Validity {
  if (value.length === 0) {
    return [true, '']
  }

  const YoutubeRegex = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const VimeoRegex = /^.*(vimeo\.com\/|videos\/|video\/|channels)([\d]+)/

  if (!YoutubeRegex.test(value) && !VimeoRegex.test(value)) {
    return [false, 'De opgegeven URL is geen geldige URL.']
  }

  return [true, '']
}

export function validateZipCode(value: string): Validity {
  if (value.length === 0) return [false, 'Vul een postcode in.']
  return [true, '']
}

export function validateCity(value: string): Validity {
  if (value.length === 0) return [false, 'Vul een plaatsnaam in.']
  return [true, '']
}

const isValidEmail = (value: any): boolean => {
  const tester = /^[\w-+.]+@([\w-]+\.)+[\w-]{2,32}$/g
  return tester.test(String(value))
}

export const validateCompany: Record<string, Validator<unknown>> = {
  logo: () => [true, ''],
  applyVia: () => [true, ''],
  company: (value: string) => {
    if (value.length === 0) {
      return [
        false,
        'Om een vacature te kunnen plaatsen moeten wij weten voor welk bedrijf en op welke locatie deze moet worden gepromoot.',
      ]
    }
    return [true, '']
  },
  address: (value: string) => {
    if (value.length === 0) return [false, 'Vul een adres in.']
    return [true, '']
  },
  zipCode: validateZipCode,
  city: (value: string) => {
    if (value.length === 0) return [false, 'Vul een plaatsnaam in.']
    return [true, '']
  },
  applyViaFirstName: (value: string, formData: Record<string, any>) => {
    if (formData.applyVia === ApplyViaType.Email) {
      /* istanbul ignore else */
      if (value.length === 0) return [false, 'Vul een voornaam in.']
    }
    return [true, '']
  },
  applyViaLastName: (value: string, formData: Record<string, any>) => {
    if (formData.applyVia === ApplyViaType.Email) {
      /* istanbul ignore else */
      if (value.length === 0) return [false, 'Vul een achternaam in.']
    }
    return [true, '']
  },
  applyViaEmail: (value: string, formData: Record<string, any>) => {
    if (formData.applyVia === ApplyViaType.Email) {
      if (value.length === 0) {
        return [false, 'Wij hebben uw e-mailadres voor sollicitaties nodig om deze door te kunnen sturen.']
      }
      const isValid = isValidEmail(value)
      /* istanbul ignore else */
      if (!isValid) return [false, 'Dit e-mailadres is helaas niet geldig.']
    }
    return [true, '']
  },
  applyViaWebsite: (value: string, formData: Record<string, any>) => {
    if (formData.applyVia === ApplyViaType.Website) {
      return validateUrl(value)
    }
    return [true, '']
  },
}

export const validateCompanyOptionalFields: Record<string, Validator<unknown>> = {
  firstName: noValidation,
  lastName: noValidation,
  email: (value: string) => {
    if (value.length === 0) {
      return [true, '']
    }
    const isValid = isValidEmail(value)
    if (!isValid) {
      return [false, 'Dit e-mailadres is helaas niet geldig.']
    }
    return [true, '']
  },
  website: (value: string) => {
    if (value !== '') {
      return validateUrl(value)
    }
    return [true, '']
  },
  phoneNumber: (value: string) => {
    if (value.length === 0) {
      return [true, '']
    }

    // eslint-disable-next-line max-len
    const phoneNumberRegex =
      /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)/
    if (!phoneNumberRegex.test(value)) {
      return [false, 'Vul een geldig telefoonnummer in.']
    }

    return [true, '']
  },
}
